import { trigger, state, style, transition, animate } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, Renderer2, signal, ViewChild, ViewChildren, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { BrowserSnifferService } from '../../services/browser-sniffer.service';
import { MapService } from '../../services/map.service';
import { ToggleEventNameType, ToggleEventService, ToggleEventType } from '../../services/toggle-event.service';
import { TraceDrawerType, TraceService } from '../../services/trace.service';
import { TraceCreateConfirmationDialogComponent, TraceCreateConfirmationDialogData } from './trace-create/trace-create-confirmation-dialog/trace-create-confirmation-dialog.component';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ENTER, SPACE, hasModifierKey } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TraceImportComponent } from './trace-import/trace-import.component';
import { TraceCreateComponent } from './trace-create/trace-create.component';
import { TraceViewComponent } from './trace-view/trace-view.component';
import { MatTabsModule } from '@angular/material/tabs';
import { BackEventService } from '@tt-lib/services/back-event.service';
import { MatBadgeModule } from '@angular/material/badge'
import { TraceSuggestionsComponent } from './trace-suggestions/trace-suggestions.component';

@Component({
  selector: 'tt-trace',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    MatTabsModule,
    TranslateModule,
    TraceImportComponent,
    TraceCreateComponent,
    TraceViewComponent,
    TraceSuggestionsComponent,
    MatBadgeModule
  ],
  templateUrl: './trace.component.html',
  styleUrls: ['./trace.scss'],
  /*
  we do it css based
  animations: [
    //Animation that slides a drawer in and out
    trigger('openClose', [
      state('open', style({
        //transform gives problem when positioning fixed elements in component (the drawingtools)
        // transform: 'translate3d(0px, 0px, 0px)',
        right: '0px',
      })),
      state('close', style({
        'box-shadow': 'none',
      })),
      transition('open => close', animate('250ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
      transition('close => open', animate('250ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
    ]),
  ],
  */
  standalone: true
})
export class TraceComponent implements OnInit, OnDestroy, AfterViewInit {

  private _mapElement: HTMLElement;
  private _subscriptions: Subscription;
  public isOpen: WritableSignal<boolean> = signal(false);
  private _olControlElements: NodeListOf<HTMLElement>;
  private _confirmationDialog: MatDialogRef<TraceCreateConfirmationDialogComponent, { data: TraceCreateConfirmationDialogData }>;
  @ViewChildren('tab') tabs: QueryList<ElementRef>;
  @ViewChild('traceCreate') private traceCreate: TraceCreateComponent;

  //dynamic template attributes (setters/getters below)
  private _hideTraceCreate: boolean = false;
  private _hideSuggestions: boolean = false;
  private _suggestionsTabTitleI8nKey: string = null;
  private _suggestionsPanelClass: string = null;
  private _suggestionsTabFontIcon: string = null;
  private _buttonFontIcon: string = null;

  //@ViewChild('tabgroup', { static: true }) tabgroup: MatTabGroup;
  @ViewChild('tabgroup', { static: true }) set tabGroup(tg: MatTabGroup) {
    if (tg) {
      //timeout to be sure the header is loaded
      setTimeout(() => {

        /*
       handleClick function to overrule the default tabheaderclick to intercept before tabchange so we can show the disclaimer and do the tabchange after closing it...
        */
        (tg as any)._handleClick = (tab: MatTab, tabHeader: any, index: number) => {
          if (tg.selectedIndex != index) {
            //disabled sessionstorage while testing...
            if (TraceDrawerType[index] === 'CREATE_ROUTE' && sessionStorage.getItem('drawdisclaimer') !== 'shown') {

              this.traceService.getDrawDisclaimerDialog().afterClosed().subscribe(() => {
                sessionStorage.setItem('drawdisclaimer', 'shown');
                //case use was dragging window to phone size when disclaimer was open
                //(!this.browserSniffer.isPhone()) ? tg.selectedIndex = index : this.traceService.onOpenCreateMobile();
                tg.selectedIndex = index
                if (this.browserSniffer.isPhone()) {
                  this.traceService.onOpenCreateMobile();
                }
              })
            }
            else {
              tg.selectedIndex = index;
            }
          }
        };

        const tabHeader = (tg as any)._tabHeader;
        tabHeader._handleKeydown = (event: KeyboardEvent) => {
          if (hasModifierKey(event)) {
            return;
          }
          switch (event.code) {
            case 'Enter':
            case 'Space':
              if (tabHeader.focusIndex !== tabHeader.selectedIndex) {
                const item = tabHeader._items.get(tabHeader.focusIndex);
                // this.traceService.sjappi(tabHeader.focusIndex, item, tabHeader);
              }
              break;
            default:
              tabHeader._keyManager.onKeydown(event);
          }
        };
      }, 1000);
    }
  }

  constructor(
    private _renderer: Renderer2,
    private _toggleEventService: ToggleEventService,
    public browserSniffer: BrowserSnifferService,
    public traceService: TraceService,
    private _mapService: MapService,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    private backEventService: BackEventService
  ) {
    this._confirmationDialog = null;
    this._subscriptions = new Subscription();

    this._subscriptions.add(
      this._toggleEventService.emitter$.subscribe((toggleEvent: ToggleEventType) => {
        switch (toggleEvent.forwardedFor) {
          case 'trace': {
            switch (toggleEvent.eventName) {
              case ToggleEventNameType.INACTIVE: {
                this._toggleEventService.toggleEvent({
                  forwardedFor: 'drawing-tools',
                  eventName: ToggleEventNameType.CLOSE_COMMAND
                });
                if (this.isOpen()) {
                  this.close();
                }
                break;
              }
              case ToggleEventNameType.CLOSE_COMMAND: {
                if (this.isOpen()) {
                  this.close();
                }
                break;
              }
              case ToggleEventNameType.OPEN_COMMAND: {
                if (!this.isOpen()) {
                  this.open();
                }
                break;
              }
            }
            break;
          }
          case 'send-map-remark-panel':
          case 'measure': {
            if (this.traceService.isActive()) {
              if (toggleEvent.eventName === ToggleEventNameType.OPEN) {
                if (this.traceService.checkForExternalMapEditingInteractions() && this.traceService.tabindex() === TraceDrawerType.CREATE_ROUTE) {
                  //other drawing tools active
                  this._toggleEventService.toggleEvent({
                    forwardedFor: 'drawing-tools',
                    eventName: ToggleEventNameType.INACTIVE_COMMAND
                  });
                }

              } else if (toggleEvent.eventName === ToggleEventNameType.CLOSE) {

                if (!this.traceService.checkForExternalMapEditingInteractions() && this.traceService.tabindex() === TraceDrawerType.CREATE_ROUTE) {
                  //no other drawing tools active anymore
                  this._toggleEventService.toggleEvent({
                    forwardedFor: 'drawing-tools',
                    eventName: ToggleEventNameType.ACTIVE_COMMAND
                  });
                }
              }
            }
            break;
          }
          case 'legend': {
            if (this.traceService.isActive()) {
              if (toggleEvent.eventName === ToggleEventNameType.OPEN) {
                this._toggleEventService.toggleEvent({
                  forwardedFor: 'drawing-tools',
                  eventName: ToggleEventNameType.CLOSE_COMMAND
                });
                if (this.isOpen()) {
                  this.close();
                }
              }
            }
            break;
          }
        }
      })
    );
  }

  ngOnInit(): void {
    this._mapElement = this._mapService.getCurrentMap().getTargetElement();
    if (this._mapElement.classList.contains('trace-drawer-open')) {
      this.isOpen.set(true);
    }

    // setTimeout(() => {
    //   this.open();
    // }, 500);
    // setTimeout(() => {
    //   this.switchTabType(TraceDrawerType.CREATE_ROUTE)
    // }, 1500);


    // handleClick function to overrule the default tabheaderclick to intercept before tabchange so we can show the disclaimer and do the tabchange after closing it...
    // (this.tabgroup as any)._handleClick = (tab: MatTab, tabHeader: any, index: number) => {
    //   if (this.tabgroup.selectedIndex != index) {
    //     //disabled sessionstorage while testing...
    //     if (TraceDrawerType[index] === 'CREATE_ROUTE' && sessionStorage.getItem('drawdisclaimer') !== 'shown') {

    //       this.traceService.getDrawDisclaimerDialog().afterClosed().subscribe(() => {
    //         sessionStorage.setItem('drawdisclaimer', 'shown');
    //         //case use was dragging window to phone size when disclaimer was open
    //         (!this.browserSniffer.isPhone()) ? this.tabgroup.selectedIndex = index : this.traceService.onOpenCreateMobile();
    //       })
    //     }
    //     else {
    //       this.tabgroup.selectedIndex = index;
    //     }
    //   }
    // };
    // setTimeout(() => {
    //   const tabHeader = (this.tabgroup as any)._tabHeader;
    //   tabHeader._handleKeydown = (event: KeyboardEvent) => {
    //     if (hasModifierKey(event)) {
    //       return;
    //     }
    //     switch (event.keyCode) {
    //       case ENTER:
    //       case SPACE:
    //         if (tabHeader.focusIndex !== tabHeader.selectedIndex) {
    //           const item = tabHeader._items.get(tabHeader.focusIndex);
    //           // this.traceService.sjappi(tabHeader.focusIndex, item, tabHeader);
    //         }
    //         break;
    //       default:
    //         tabHeader._keyManager.onKeydown(event);
    //     }
    //   };
    // }, 1000);



    // if (document.getElementsByTagName('body')[0].classList.contains('elevation-profile-open')) {
    //   this._renderer.addClass(elm, 'trace-drawer-open');
    // }
  }
  ngAfterViewInit() {
    this._olControlElements = document.querySelectorAll<HTMLElement>(
      'body, tt-zoom, .elevation-profile-button-container, .baselayer-selector-button-container, .trace-select-button-container,  .legend-button-container, .remarks-select-button-container,.measure-button-container, .ol-scale-line, tt-mouse-position,  tt-mouse-position-mapindex'
    );
    this.traceService.setCanOpenTraceTabs();
  }
  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    //automatic shutdown (direct call to this.traceService.shutDown()) is managed in tmv.component.ts so that it also works on phones
    //manual shutdowns are managed via method shutDown() below
  }

  @Input()
  set hideTraceCreate(val: string) {
    this._hideTraceCreate = val ? val.trim().toLowerCase() === "true" : false;
  }
  get hideTraceCreate(): boolean {
    return this._hideTraceCreate;
  }

  @Input()
  set hideSuggestions(val: string) {
    this._hideSuggestions = val ? val.trim().toLowerCase() === "true" : false;
  }
  get hideSuggestions(): boolean {
    return this._hideSuggestions;
  }

  @Input()
  set suggestionsTabTitleI8nKey(val: string) {
    this._suggestionsTabTitleI8nKey = val ? val : null;
  }
  get suggestionsTabTitleI8nKey(): string {
    return this._suggestionsTabTitleI8nKey;
  }

  @Input()
  set suggestionsPanelClass(val: string) {
    this._suggestionsPanelClass = val ? val : null;
  }
  get suggestionsPanelClass(): string {
    return this._suggestionsPanelClass;
  }

  @Input()
  set suggestionsTabFontIcon(val: string) {
    this._suggestionsTabFontIcon = val ? val : null;
  }
  get suggestionsTabFontIcon(): string {
    return this._suggestionsTabFontIcon;
  }

  @Input()
  set buttonFontIcon(val: string) {
    this._buttonFontIcon = val ? val : null;
  }
  get buttonFontIcon(): string {
    return this._buttonFontIcon;
  }

  toggle() {
    this.isOpen() ? this.close() : this.openTraceDrawer(null);
  }

  openTraceDrawer(traceDrawerTypeAsString?: keyof typeof TraceDrawerType) {
    this.traceService.openTraceTab(traceDrawerTypeAsString ? TraceDrawerType[traceDrawerTypeAsString] : null);
  }

  open() {
    // this._renderer.setStyle(this._mapElement, 'width', 'calc(100% - 360px)');
    this._renderer.addClass(this._mapElement, 'trace-drawer-open');
    this._olControlElements.forEach((elm) => {
      this._renderer.addClass(elm, 'trace-drawer-open');
    });
    // document.getElementsByTagName('body')[0].classList.add('trace-drawer-open');
    this.isOpen.set(true);
    //when opening drawer with create tab active
    if (this.traceService.tabindex() == TraceDrawerType.CREATE_ROUTE) {
      this.switchTabType(TraceDrawerType.CREATE_ROUTE);
    }
    this.traceService.setWatchingRouteWithoutDrawer(false);
    this._toggleEventService.toggleEvent({
      eventName: ToggleEventNameType.OPEN,
      forwardedFor: 'trace',
    });

    if (this.traceService.tabindex() == TraceDrawerType.CREATE_ROUTE && this.traceService.checkForExternalMapEditingInteractions()) {
      //other drawing tools active
      this._toggleEventService.toggleEvent({
        forwardedFor: 'drawing-tools',
        eventName: ToggleEventNameType.INACTIVE_COMMAND
      });
    }

    this.backEventService.registerOpenedWindow({ windowName: 'trace', isAutoCloseableWindow: false });
  }

  close() {

    this._olControlElements.forEach((elm) => {
      this._renderer.removeClass(elm, 'trace-drawer-open');
    });
    // this._renderer.setStyle(this._mapElement, 'width', '100%');
    this._renderer.removeClass(this._mapElement, 'trace-drawer-open')
    this.isOpen.set(false);
    this.traceService.setWatchingRouteWithoutDrawer(true);

    this._toggleEventService.toggleEvent({
      eventName: ToggleEventNameType.CLOSE,
      forwardedFor: 'trace',
    });

    //the physical shutdown button is not shown anymore in the template, so extra test here
    if (this.traceService.canShutDownSafely()) {
      this.traceService.shutDown();
    }

  }

  switchTabType(drawerType: TraceDrawerType, traceCreateIsHidden?: boolean, traceSuggestionsIsHidden?: boolean) {

    if (drawerType !== this.traceService.tabindex()) {
      if (drawerType >= TraceDrawerType.CLOSE_AND_VIEW_ROUTE) {
        console.log(`switchTabType: we blocked switch to tab index ${drawerType} , highest possible tab index is ${TraceDrawerType.CLOSE_AND_VIEW_ROUTE - 1}`);
        return;
      }

      if ((traceCreateIsHidden && drawerType === TraceDrawerType.CREATE_ROUTE) || (traceSuggestionsIsHidden && drawerType === TraceDrawerType.SUGGESTIONS_ROUTE)) {
        console.log(`switchTabType: we blocked switch to tab index ${drawerType} , tab is hidden`);
        return;
      }

      if (this.traceService.tabindex() === TraceDrawerType.SUGGESTIONS_ROUTE) {
        this.traceService.hideSuggestionsPreview();
      }

      //console.log(`switchTabType: ${this.traceService.tabindex()} > ${drawerType}`)
      this.traceService.tabindex.set(drawerType);
    }

    switch (drawerType) {
      case TraceDrawerType.VIEW_ROUTE:
      case TraceDrawerType.IMPORT_ROUTE: {
        if (this.traceService.isInEditingMode()) {
          this.traceService.setInEditingMode(false);
        }
        break;
      }
      case TraceDrawerType.CREATE_ROUTE: {
        this._toggleEventService.toggleEvent({
          forwardedFor: 'drawing-tools',
          eventName: ToggleEventNameType.OPEN_COMMAND
        });
        //if necessary this method will also call traceService.setInEditingMode()
        this.traceCreate.onOpenTraceCreate();
        break;
      }
    }

    if (drawerType !== TraceDrawerType.CREATE_ROUTE) {
      this.traceService.setInEditingMode(false);
      this._toggleEventService.toggleEvent({
        forwardedFor: 'drawing-tools',
        eventName: ToggleEventNameType.CLOSE_COMMAND
      });
    }
  }

  /*
  not using the close button anymore in the template
  shutDown(ev) {
    ev.stopPropagation();   // prevent mat-tab-group from handling the click
    ev.preventDefault();
    if (this.traceService.hasTraceRouteOrWaypointOnMap()) {
      if (this._confirmationDialog !== null) {
        this._confirmationDialog.close();
        this._confirmationDialog = null;
      }
      this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
        panelClass: 'small-dialog',
        data: {
          title: this._translateService.instant('trace.shutdown'),
          text: this._translateService.instant('trace.confirm-shutdown'),
          type: 'shutDown'
        },
      });
      this._confirmationDialog.afterClosed().pipe(take(1)).subscribe(result => {
        if (result && result.data && result.data.type === 'shutDown') {
          this.traceService.shutDown();
        }
        this._confirmationDialog = null;
      });
      this.backEventService.registerOpenedWindow({windowName: 'TraceCreateConfirmationDialogComponent', isAutoCloseableWindow: true});
    } else {
      this.traceService.shutDown();
    }
  }
  */


}
